import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Input,
  Text,
  VStack,
  ButtonGroup,
} from '@chakra-ui/react';

const SCRIPT_FONT = '"Brush Script MT", "Segoe Script", cursive';
const INK = '#14213d';

/**
 * Type-or-draw signature input. Always reports a transparent PNG data URL
 * (or null when empty) via onChange, matching what the backend expects to
 * stamp onto the estimate PDF.
 */
function SignaturePad({ signerName, onChange }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('type');
  const [typed, setTyped] = useState(signerName || '');
  const drawing = useRef(false);
  const last = useRef(null);

  useEffect(() => {
    if (mode !== 'type') return;
    const text = typed.trim();
    if (!text) {
      onChange(null);
      return;
    }
    onChange(renderTyped(text));
  }, [mode, typed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = INK;
    onChange(null);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const point = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = e => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };

  const move = e => {
    if (!drawing.current || !last.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    onChange(cropTransparent(canvasRef.current).toDataURL('image/png'));
  };

  const clear = () => {
    if (mode === 'type') {
      setTyped('');
      return;
    }
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <VStack align="stretch" spacing={2} w="100%">
      <HStack justify="space-between">
        <ButtonGroup size="sm" isAttached>
          <Button
            variant={mode === 'type' ? 'solid' : 'outline'}
            colorScheme="blue"
            onClick={() => setMode('type')}
          >
            Type
          </Button>
          <Button
            variant={mode === 'draw' ? 'solid' : 'outline'}
            colorScheme="blue"
            onClick={() => setMode('draw')}
          >
            Draw
          </Button>
        </ButtonGroup>
        <Button size="sm" variant="ghost" onClick={clear}>
          Clear
        </Button>
      </HStack>

      {mode === 'type' ? (
        <Box
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          p={3}
        >
          <Input
            aria-label="Your full name"
            value={typed}
            maxLength={80}
            onChange={e => setTyped(e.target.value)}
            mb={2}
            size="sm"
          />
          <Box
            borderBottom="1px solid"
            borderColor="gray.300"
            minH="60px"
            display="flex"
            alignItems="center"
            fontSize="32px"
            fontFamily={SCRIPT_FONT}
            color={INK}
          >
            {typed.trim() || (
              <Text color="gray.400" fontSize="sm" fontFamily="inherit">
                Signature preview
              </Text>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          p={2}
        >
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: '140px',
              touchAction: 'none',
              cursor: 'crosshair',
            }}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
          <Text fontSize="xs" color="gray.500" textAlign="center">
            Draw your signature above
          </Text>
        </Box>
      )}
    </VStack>
  );
}

function renderTyped(text) {
  const canvas = document.createElement('canvas');
  const measure = canvas.getContext('2d');
  measure.font = `48px ${SCRIPT_FONT}`;
  const width = Math.ceil(measure.measureText(text).width) + 40;
  canvas.width = Math.min(1200, Math.max(200, width));
  canvas.height = 100;
  const ctx = canvas.getContext('2d');
  ctx.font = `48px ${SCRIPT_FONT}`;
  ctx.fillStyle = INK;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, 55, canvas.width - 40);
  return canvas.toDataURL('image/png');
}

function cropTransparent(source) {
  const ctx = source.getContext('2d');
  const { width, height } = source;
  const pixels = ctx.getImageData(0, 0, width, height).data;
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX) return source;
  const pad = 10;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const out = document.createElement('canvas');
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  out
    .getContext('2d')
    .drawImage(
      source,
      minX,
      minY,
      out.width,
      out.height,
      0,
      0,
      out.width,
      out.height
    );
  return out;
}

export default SignaturePad;
